import type { FC } from 'react'

import { Td } from '@skytech/ui/Table/Td'
import { isNil } from '@skytech/utils'
import { Link } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'

import { useTranslation } from '@/components/i18n/hooks'
import { Translation } from '@/components/i18n/Translation'
import { LocalDate } from '@/components/LocalDate'
import { TrLink } from '@/components/TrLink'
import Highlight from '@/components/util/Highlight'
import { WarningIcon } from '@/components/util/WarningIcon'
import { appRoutes } from '@/util/misc'

import { getIsOrderOpen } from '../helpers'
import type { SubOrder } from '../resources'
import { isPortingOrder, isTerminationOrder, isUpgradeOrder } from '../types'

export const SubscriptionOrderListItem: FC<
  { textSearch: string; isUpgrade?: boolean } & SubOrder
> = ({ textSearch, isUpgrade = false, ...order }) => {
  const translate = useTranslation()

  const {
    id,
    user,
    customer,
    code,
    orderUpdate,
    orderDate,
    subscriptionServiceName,
    subStatus,
    dealerComment,
    userComment,
  } = order
  const hasComment = Boolean(dealerComment?.trim()) || Boolean(userComment?.trim())

  return (
    <TrLink
      className="group relative hover:z-20"
      path={appRoutes.subscriptionOrder.replace(':order_id', String(id))}
    >
      <Td className="text-xl md:p-2 lg:p-2.5 xl:p-4">
        <Link to={appRoutes.subscriptionOrder.replace(':order_id', String(id))}>
          <Highlight pattern={textSearch} text={String(id)} />
        </Link>

        {hasComment && (
          <div className="shadow-basic pointer-events-none absolute left-1 top-11 w-[40rem] max-w-[calc(100vw-4rem)] whitespace-pre-wrap rounded-lg bg-white p-4 pt-3.5 text-left opacity-0 transition-all duration-[250ms] ease-in-out group-hover:pointer-events-auto group-hover:z-10 group-hover:opacity-100">
            {Boolean(dealerComment?.trim()) && (
              <>
                <h4 className="m-0 mb-1 text-xs font-normal opacity-70">
                  <Translation id="Customer comment" />
                </h4>
                <p className="mx-0 -mb-1 mt-1 text-sm">{dealerComment}</p>
              </>
            )}

            {Boolean(userComment?.trim()) && (
              <>
                <h4 className="m-0 mb-1 mt-3 text-xs font-normal opacity-70">
                  <Translation id="Your comment" />
                </h4>
                <p className="mx-0 -mb-1 mt-1 text-sm">{userComment}</p>
              </>
            )}
          </div>
        )}
        <div className="mt-1.5 flex flex-col text-xs text-gray-400 min-[500px]:hidden">
          <Highlight pattern={textSearch} text={customer || undefined} />

          <Highlight pattern={textSearch} text={user || undefined} />

          <Highlight pattern={textSearch} text={code || undefined} />
        </div>
      </Td>

      <Td className="hidden text-sm min-[500px]:table-cell md:p-2 lg:p-2.5 xl:p-4">
        <Highlight pattern={textSearch} text={customer || undefined} />

        <div className="mt-1.5 table-cell text-gray-400 sm:hidden">
          <Highlight pattern={textSearch} text={user || undefined} />

          <div className="mt-[0.2em] text-[0.8rem] text-gray-400">
            <Highlight pattern={textSearch} text={code || undefined} />
          </div>
        </div>
      </Td>

      <Td className="hidden text-sm sm:table-cell md:p-2 lg:p-2.5 xl:p-4">
        <div>
          <Highlight pattern={textSearch} text={user || undefined} />
        </div>

        <div className="mt-[0.2em] text-[0.8rem] text-gray-400">
          <Highlight pattern={textSearch} text={code || undefined} />
        </div>
      </Td>

      <Td className="text-sm md:p-2 lg:p-2.5 xl:p-4">
        <Highlight
          pattern={textSearch}
          text={translate(`STATUS_SUBSCRIPTION_ORDER/${subStatus}`)}
        />
        {isUpgrade && (
          <div className="mt-1.5 flex text-gray-400 md:hidden">
            <Highlight pattern={textSearch} text={subscriptionServiceName || undefined} />
          </div>
        )}
      </Td>

      {isPortingOrder(order) && (
        <Td className="text-sm md:p-2 lg:p-2.5 xl:p-4">
          <div className="flex content-between items-center">
            {Boolean(order.activationDate) && <LocalDate date={order.activationDate} />}
            {order.daysToActivation != null &&
              order.daysToActivation >= 0 &&
              order.daysToActivation < 10 &&
              getIsOrderOpen(subStatus) && <WarningIcon isUrgent={order.daysToActivation < 5} />}
          </div>
        </Td>
      )}

      {isTerminationOrder(order) && (
        <>
          <Td className="hidden text-sm md:table-cell md:p-2 lg:p-2.5 xl:p-4">
            <Highlight
              pattern={textSearch}
              text={
                !isNil(order.terminationTypeId)
                  ? translate(`TYPE_TERMINATION/${order.terminationTypeId}`)
                  : undefined
              }
            />
          </Td>

          <Td className="text-sm md:p-2 lg:p-2.5 xl:p-4">
            <div className="flex content-between items-center">
              <LocalDate date={order.terminationDate} />

              {order.daysToTermination &&
                order.daysToTermination < 10 &&
                getIsOrderOpen(subStatus) && <WarningIcon isUrgent={order.daysToTermination < 5} />}
            </div>
          </Td>
        </>
      )}

      {isUpgradeOrder(order) && (
        <Td className="hidden text-sm md:table-cell md:p-2 lg:p-2.5 xl:p-4">
          <Highlight pattern={textSearch} text={subscriptionServiceName || undefined} />
        </Td>
      )}

      <Td
        className={twMerge(
          'hidden text-sm md:table-cell md:p-2 lg:p-2.5 xl:p-4',
          isUpgrade && 'table-cell'
        )}
      >
        <LocalDate date={orderDate} />
      </Td>

      <Td className="hidden text-sm md:table-cell md:p-2 lg:p-2.5 xl:p-4">
        <LocalDate date={orderUpdate} />
      </Td>
    </TrLink>
  )
}
